import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { User } from 'firebase/auth';
import { effectiveActiveRole, parseModules, parseRoles, sameModules, type ModuleLite, type RoleLite } from '@blauturquesa/shared/roles-lite';
import i18n from '../../i18n';

export type AuthState =
  | { status: 'loading' }
  | { status: 'signedOut' }
  | {
      status: 'signedIn';
      user: User;
      /** Perfils concedits (§2.2). */
      roles: RoleLite[];
      /** Perfil actiu efectiu; null si el token encara no en porta (usuari acabat de crear). */
      activeRole: RoleLite | null;
      /** Mòduls concedits pel superadmin (§2.3). */
      modules: ModuleLite[];
      /** Ja s'ha comprovat el perfil amb el servidor en aquesta sessió (o ha fallat i seguim igualment). */
      profileReady: boolean;
    };

const AuthContext = createContext<AuthState>({ status: 'loading' });

export function useAuth(): AuthState {
  return useContext(AuthContext);
}

/** Espera que el navegador estigui lliure: la pantalla es pinta abans de carregar Firebase (§10.4). */
function whenIdle(): Promise<void> {
  return new Promise((resolve) => {
    if ('requestIdleCallback' in window) window.requestIdleCallback(() => resolve(), { timeout: 1500 });
    else setTimeout(resolve, 200);
  });
}

function sameRoles(a: readonly RoleLite[], b: readonly RoleLite[]): boolean {
  return a.length === b.length && a.every((r, i) => r === b[i]);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;
    let checkedUid: string | null = null;

    void (async () => {
      await whenIdle();
      const [{ auth }, { onIdTokenChanged }] = await Promise.all([import('../../firebase/auth'), import('firebase/auth')]);
      if (cancelled) return;

      unsubscribe = onIdTokenChanged(auth, async (user) => {
        if (!user) {
          checkedUid = null;
          setState({ status: 'signedOut' });
          return;
        }
        const token = await user.getIdTokenResult();
        const roles = parseRoles(token.claims['roles']);
        const activeRole = effectiveActiveRole(token.claims['activeRole'], roles);
        const modules = parseModules(token.claims['modules']);
        const profileReady = checkedUid === user.uid;
        setState({ status: 'signedIn', user, roles, activeRole, modules, profileReady });
        if (profileReady) return;

        checkedUid = user.uid;
        try {
          // Crea el perfil si cal i retorna els perfils vigents al servidor. Si no coincideixen amb
          // el token (usuari nou, o el superadmin n'ha canviat algun), es força un token nou.
          const { call } = await import('../../firebase/functions');
          const server = await call<{ locale: string }, { roles: RoleLite[]; activeRole: RoleLite; modules?: ModuleLite[] }>(
            'ensureUserProfile',
            { locale: i18n.language },
          );
          if (!sameRoles(server.roles, roles) || server.activeRole !== activeRole || !sameModules(parseModules(server.modules), modules)) {
            await user.getIdToken(true);
            return;
          }
        } catch {
          // Falla a favor de l'usuari (§10.1): l'app continua; la seguretat la fan les regles.
        }
        setState({ status: 'signedIn', user, roles, activeRole, modules, profileReady: true });
      });
    })();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}
